import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { CombinationButtons } from '@interlayer/js/app/components/main/DefaultMenu/components/MenuItem/components/CombinationButtons';
import { Link } from '@interlayer/js/components/common/Link';

import { getCleanUrl } from '@core/js/utils/url.util';

import { useSwitchAccountMenu, useSwitchMobileMenu } from '@interlayer/js/app/components/main/DefaultHeader/components/HeaderMobile/hooks';
import { TYPES_BLOCK } from '@interlayer/js/app/components/main/DefaultMenu/components/MenuItem/constants';
import { MENU_CLASSES } from '@interlayer/js/app/components/main/DefaultMenu/constants';
import { useGameCodeLaunch } from '@interlayer/js/app/components/main/Games/hooks/useGameLaunch';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { getUnitForPage } from '@interlayer/js/app/utils/dy.util';
import { useLogout } from '@interlayer/js/hooks/auth/hooks/useLogout';

import { TMenuItemProps, TRenderMenuElements } from './types';

import style from './MenuItem.scss';

const isActiveMenu = (urlItem: string) => urlItem === getCleanUrl() && style.isActive;

export const MenuItem: React.FC<TMenuItemProps> = ({ data, typeBlock }) => {
    const [openedSubMenuItems, setOpenedSubMenuItems] = useState<Record<string, boolean>>({});
    const [dTagShowLeftRightMobMenuIcons] = useDynamicTags([DYNAMIC_TAG_NAMES.showLeftRightMobMenuIcons]);

    const logout = useLogout();
    const switchAccountMenu = useSwitchAccountMenu();
    const { pathname } = useLocation();
    const switchMobileMenu = useSwitchMobileMenu();
    const gameLaunch = useGameCodeLaunch('Bingo_Lobby');

    const switchMenu = () => {
        if (typeBlock === TYPES_BLOCK.right) {
            switchAccountMenu(false);
        } else {
            switchMobileMenu(false);
        }
    };

    const renderIcon = (menuIcon: string): JSX.Element | null => {
        if (!menuIcon || dTagShowLeftRightMobMenuIcons !== 'true') {
            return null;
        }

        return (
            <SvgIcon
                type={menuIcon}
                className={clsx(
                    style.itemIcon,
                    {
                        [style.defaultIconColor]: data.cssClasses.replace(/color-scheme-override/g, '').includes('color-scheme-')
                    }
                )}
            />
        );
    };

    const renderMenuElements = ({ cssClasses, url, name, children, isSubMenu, menuIcon }: TRenderMenuElements) => {
        const isLogout = cssClasses.includes(MENU_CLASSES.IS_LOGOUT);
        const isShowBingoLobby = cssClasses.includes(MENU_CLASSES.SHOW_BINGO_LOBBY);
        const isStayOnCurrentPage = isLogout || isShowBingoLobby || Boolean(children?.length);
        const isActiveItem = children?.length ? '' : isActiveMenu(url);

        const handleOpenSubItems = () => {
            setOpenedSubMenuItems({
                ...openedSubMenuItems,
                [url]: !openedSubMenuItems[url]
            });
        };

        const handlerClickMenu = () => {
            if (isLogout) {
                logout();
                switchMenu();
                return;
            }

            if (children?.length) {
                handleOpenSubItems();
                return;
            }

            if (isShowBingoLobby) {
                gameLaunch();
            }

            switchMenu();
        };

        return (
            <div className={clsx(style.itemWrap, { [style.itemBorder]: isSubMenu })} key={url}>
                <div className={clsx(style.linkContainer, isActiveItem)}>
                    <Link
                        className={clsx(style.item)}
                        data-dy-unit={getUnitForPage(url)}
                        href={isStayOnCurrentPage || Boolean(children?.length) ? pathname : url}
                        onClick={handlerClickMenu}
                    >
                        {renderIcon(menuIcon)}

                        {name}
                    </Link>

                    {Boolean(children?.length) && (
                        <div className={style.iconContainer} onClick={handleOpenSubItems}>
                            <SvgIcon
                                type="arrow-down"
                                className={clsx(style.menuItemArrow, { [style.rotateIcon]: openedSubMenuItems[url] })}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (data.cssClasses.includes(MENU_CLASSES.IS_LOGIN_AND_REG_BTN)) {
        return (
            <CombinationButtons
                title={data.title}
                icon={renderIcon(data.menuIcon)}
            />
        );
    }

    return (
        <>
            {renderMenuElements({
                cssClasses: data.cssClasses,
                url: data.url,
                name: data.name,
                menuIcon: data.menuIcon,
                children: data.children
            })}

            {Boolean(data.children?.length) && (
                <div className={clsx(style.container, { hidden: !openedSubMenuItems[data.url] })}>
                    {data.children.map(item => renderMenuElements({
                        cssClasses: item.cssClasses,
                        url: item.url,
                        name: item.name,
                        isSubMenu: true,
                        menuIcon: item.menuIcon,
                        children: []
                    }))}
                </div>
            )}
        </>
    );
};
